import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {App, Modal, Segmented, Spin, Typography} from 'antd';
import {CandlestickData} from '../../pages/trading/components/CandlestickChart';
import {klineBarsFromCandles} from '../charts/KLineChart';
import LightweightCandlestickChart from '../charts/LightweightCandlestickChart';
import {TechnicalIndicatorData, tradingService} from '../../services/tradingService';
import CompactIndicatorBar from '../../pages/trading/components/CompactIndicatorBar';
import {IndicatorConfig, TechnicalIndicator, useChartState} from '../../hooks/useChartState';
import {useKlineTimer} from '../../hooks/useKlineTimer';
import '../../pages/trading/components/ChartModalEnhanced.css';

const {Text} = Typography;

/**
 * 技术指标颜色常量（与LightweightCandlestickChart.tsx中实际使用的颜色保持一致）
 * EMA/RSI 不同周期按升序取对应颜色数组中的颜色
 * KDJ 不同周期取对应色系的 K 线颜色作为代表色
 */
const INDICATOR_COLORS = {
    EMA: ['#F0B429', '#00b4d8'],
    RSI: ['#9254de', '#d46b08'],
    BOLL_UPPER: '#f5222d',
    BOLL_MIDDLE: '#f5a623',
    BOLL_LOWER: '#00d97e',
    MACD_DIF: '#fa541c',
    MACD_DEA: '#a78bfa',
    KDJ_K: ['#f5a623', '#13c2c2', '#eb2f96'],
} as const;

const normalizeTimestampMs = (value: unknown): number | undefined => {
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num) || num <= 0) return undefined;
    if (num < 1_000_000_000_000) return Math.floor(num * 1000);
    return Math.floor(num);
};

const normalizeNumber = (value: unknown): number | undefined => {
    if (value === null || value === undefined) return undefined;
    if (typeof value === 'string' && value.trim() === '') return undefined;
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num)) return undefined;
    return num;
};

const transformIndicatorsForChart = (rawIndicators: Record<string, any> | undefined) => {
    if (!rawIndicators) return undefined;

    const result: Record<string, Array<{
        time: number;
        value?: number;
        upper?: number;
        middle?: number;
        lower?: number;
        diff?: number;
        signal?: number;
        histogram?: number;
        k?: number;
        d?: number;
        j?: number;
    }>> = {};

    Object.entries(rawIndicators).forEach(([key, indicator]) => {
        const values = indicator?.data?.values || indicator?.values;
        if (!values || !Array.isArray(values)) return;

        values.forEach((v: any) => {
            const multiPeriod = v.multiPeriodValues;
            if (!multiPeriod || typeof multiPeriod !== 'object') return;

            const timestamp = normalizeTimestampMs(v.timestamp ?? v.time);
            if (!timestamp) return;

            Object.entries(multiPeriod).forEach(([periodKey, periodData]: [string, any]) => {
                if (periodData === null || periodData === undefined) return;
                const seriesKey = `${key}_${periodKey}`;

                if ('MACD' === key) {
                    if (typeof periodData !== 'object') return;
                    const diff = normalizeNumber(periodData.diff ?? periodData.dif);
                    const signal = normalizeNumber(periodData.dea ?? periodData.signal);
                    const histogram = normalizeNumber(periodData.macd ?? periodData.histogram);
                    if (diff === undefined && signal === undefined && histogram === undefined) return;
                    if (!result[seriesKey]) result[seriesKey] = [];
                    result[seriesKey].push({
                        time: timestamp,
                        diff,
                        signal,
                        histogram,
                    });
                } else if ('BOLL' === key) {
                    if (typeof periodData !== 'object') return;
                    const upper = normalizeNumber(periodData.upper);
                    const middle = normalizeNumber(periodData.middle);
                    const lower = normalizeNumber(periodData.lower);
                    if (upper === undefined && middle === undefined && lower === undefined) return;
                    if (!result[seriesKey]) result[seriesKey] = [];
                    result[seriesKey].push({
                        time: timestamp,
                        upper,
                        middle,
                        lower,
                    });
                } else if ('KDJ' === key) {
                    if (typeof periodData !== 'object') return;
                    const k = normalizeNumber(periodData.k);
                    const d = normalizeNumber(periodData.d);
                    const j = normalizeNumber(periodData.j);
                    if (k === undefined && d === undefined && j === undefined) return;
                    if (!result[seriesKey]) result[seriesKey] = [];
                    result[seriesKey].push({
                        time: timestamp,
                        k,
                        d,
                        j,
                    });
                } else {
                    const value = normalizeNumber(periodData);
                    if (value === undefined) return;
                    if (!result[seriesKey]) result[seriesKey] = [];
                    result[seriesKey].push({
                        time: timestamp,
                        value,
                    });
                }
            });
        });
    });

    Object.values(result).forEach(arr => arr.sort((a, b) => a.time - b.time));
    return result;
};

export type ActiveChartItem =
    | {
    type: 'position';
    instId: string;
    posSide: string;
    ctime: number | string;
    referencePrice?: number;
    detail: any;
}
    | {
    type: 'order';
    instId: string;
    posSide: string;
    ctime: number | string;
    referencePrice?: number;
    detail: any;
};

type TimeFrame = '1m' | '5m' | '1h' | '4h' | '1d';
type SampleSize = 30 | 60 | 90 | 120 | 150;

/** 初始化默认技术指标配置 */
const initializeDefaultIndicators = (
    onUpdateConfig: (indicator: TechnicalIndicator, config: Partial<IndicatorConfig>) => void,
    onToggle: (indicator: TechnicalIndicator) => void
) => {
    const defaults: Array<{ type: TechnicalIndicator; config: Partial<IndicatorConfig> }> = [
        {
            type: 'EMA',
            config: {
                periods: [12, 26],
                visiblePeriods: [12, 26],
                periodColors: ['#F0B429', '#00b4d8'],
            }
        },
        {
            type: 'RSI',
            config: {
                periods: [9, 14],
                visiblePeriods: [9, 14],
                periodColors: ['#9254de', '#d46b08'],
            }
        },
        {
            type: 'BOLL',
            config: {
                periods: [20],
                visiblePeriods: [20],
                periodColors: ['#f5a623'],
                stdDevs: [2],
            }
        },
        {
            type: 'MACD',
            config: {fastPeriod: 12, slowPeriod: 26, signalPeriod: 9, period: 12}
        },
        {
            type: 'KDJ',
            config: {
                periods: [9, 14, 21],
                visiblePeriods: [9, 14, 21],
                periodColors: ['#f5a623', '#13c2c2', '#eb2f96'],
            }
        },
    ];
    defaults.forEach(({type, config}) => {
        onUpdateConfig(type, config);
        onToggle(type);
    });
};

interface ActivePositionOrderChartModalProps {
    visible: boolean;
    onClose: () => void;
    item: ActiveChartItem | null;
    apiKeyId: number;
}

const ActivePositionOrderChartModal: React.FC<ActivePositionOrderChartModalProps> = ({
                                                                                         visible,
                                                                                         onClose,
                                                                                         item,
                                                                                         apiKeyId
                                                                                     }) => {
    const {message: messageApi} = App.useApp();
    const [chartData, setChartData] = useState<CandlestickData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [currentTimeFrame, setCurrentTimeFrame] = useState<TimeFrame>('5m');
    const [manualTimeFrame, setManualTimeFrame] = useState<TimeFrame | undefined>(undefined);
    const [currentLimit, setCurrentLimit] = useState<SampleSize>(120);
    const [manualLimit, setManualLimit] = useState<SampleSize | undefined>(undefined);
    const [currentEntryCandleTime, setCurrentEntryCandleTime] = useState<number | undefined>(undefined);
    const [showTPSL, setShowTPSL] = useState<boolean>(false);
    const [showAvgPx, setShowAvgPx] = useState<boolean>(true);
    const [tpslReloadKey, setTpslReloadKey] = useState<number>(0);
    const [paneReloadKey, setPaneReloadKey] = useState<number>(0);
    const [indicators, setIndicators] = useState<Record<string, TechnicalIndicatorData> | undefined>(undefined);
    const [currentMarkPrice, setCurrentMarkPrice] = useState<number | null>(null);
    // 支撑&阻力线状态
    const [showPivotPoints, setShowPivotPoints] = useState<boolean>(false);
    const [pivotPointsData, setPivotPointsData] = useState<{
        pivot: number;
        periodStartTime?: number;
        supports: Array<{ price: number; basedOn: string; basedTime: number }>;
        resistances: Array<{ price: number; basedOn: string; basedTime: number }>;
    } | null>(null);
    const [loadingPivot, setLoadingPivot] = useState<boolean>(false);

    // useChartState 管理技术指标（与页面K线弹窗一致）
    const {
        selectedIndicators,
        indicatorConfigs,
        toggleIndicator,
        updateIndicatorConfig,
        clearAllIndicators,
    } = useChartState(item?.instId);

    // 标记默认指标是否已初始化
    const initializedRef = useRef(false);

    // 打开弹窗时初始化默认技术指标
    useEffect(() => {
        if (visible && item?.instId && !initializedRef.current) {
            if (0 === selectedIndicators.length) {
                initializeDefaultIndicators(updateIndicatorConfig, toggleIndicator);
            }
            initializedRef.current = true;
        }
        if (!visible) {
            initializedRef.current = false;
        }
    }, [visible, item?.instId]);

    const bars = useMemo(() => klineBarsFromCandles(chartData), [chartData]);

    // 可见指标状态（从useChartState计算）
    const visibleIndicators = useMemo(() => {
        const result: Record<string, boolean> = {};
        selectedIndicators.forEach(indicator => {
            const config = indicatorConfigs[indicator];
            if ('MACD' === indicator) {
                const fastPeriod = config?.fastPeriod ?? config?.period ?? 12;
                const slowPeriod = config?.slowPeriod ?? 26;
                const signalPeriod = config?.signalPeriod ?? 9;
                if (fastPeriod && slowPeriod && signalPeriod) {
                    result[`MACD_${fastPeriod}_${slowPeriod}_${signalPeriod}`] = true;
                }
                return;
            }
            const periodsToShow = config?.visiblePeriods !== undefined
                ? config.visiblePeriods
                : (config?.periods || []);
            periodsToShow.forEach(period => {
                result[`${indicator}_${period}`] = true;
            });
        });
        return result;
    }, [selectedIndicators, indicatorConfigs]);

    // 价格覆盖层计算
    const displayPrice = currentMarkPrice || normalizeNumber(item?.referencePrice);
    const priceChangePercent = useMemo(() => {
        if (!bars || bars.length < 1) return null;
        const last = bars[bars.length - 1];
        if (null == last || null == last.open || 0 === last.open) return null;
        return ((last.close - last.open) / last.open) * 100;
    }, [bars]);
    const isPriceUp = useMemo(() => {
        if (!bars || bars.length < 1) return true;
        const last = bars[bars.length - 1];
        return last.close >= last.open;
    }, [bars]);

    // 获取 Pivot Points 数据
    const fetchPivotPoints = async () => {
        if (!item?.instId || loadingPivot) return;
        setLoadingPivot(true);
        try {
            const data = await tradingService.getPivotPoints({
                instId: item.instId,
                timeframe: currentTimeFrame,
                limit: currentLimit,
                apiKeyId,
            });
            setPivotPointsData(data);
            setShowPivotPoints(true);
        } catch (e) {
        } finally {
            setLoadingPivot(false);
        }
    };

    // 监听副图指标变化，当所有副图指标都取消后强制重新创建图表
    useEffect(() => {
        const hasOscillator = Object.keys(visibleIndicators).some(key => {
            if (!visibleIndicators[key]) return false;
            const baseType = key.split('_')[0];
            return ['MACD', 'KDJ', 'RSI'].includes(baseType);
        });
        if (!hasOscillator) {
            setPaneReloadKey(k => k + 1);
        }
    }, [visibleIndicators]);

    // 构建技术指标API请求参数
    const buildIndicatorParams = useCallback(() => {
        const params: {
            indicators?: string[];
            emaPeriods?: number[];
            smaPeriods?: number[];
            wmaPeriods?: number[];
            rsiPeriods?: number[];
            bollParams?: string[];
            macdPeriods?: number[];
            kdjPeriods?: number[];
            cciPeriods?: number[];
            atrPeriods?: number[];
            obvPeriods?: number[];
            adxPeriods?: number[];
        } = {};
        if (0 === selectedIndicators.length) return params;

        params.indicators = selectedIndicators;

        selectedIndicators.forEach(indicator => {
            const config = indicatorConfigs[indicator];
            const visibleOrAll = () => config?.visiblePeriods && config.visiblePeriods.length > 0
                ? config.visiblePeriods
                : (config?.periods || []);
            if ('EMA' === indicator) {
                const periods = visibleOrAll();
                if (periods.length > 0) params.emaPeriods = periods;
            } else if ('SMA' === indicator) {
                const periods = visibleOrAll();
                if (periods.length > 0) params.smaPeriods = periods;
            } else if ('WMA' === indicator) {
                const periods = visibleOrAll();
                if (periods.length > 0) params.wmaPeriods = periods;
            } else if ('RSI' === indicator) {
                const periods = visibleOrAll();
                if (periods.length > 0) params.rsiPeriods = periods;
            } else if ('BOLL' === indicator) {
                const periods = config?.periods || [];
                const stdDevs = config?.stdDevs || [];
                if (periods.length > 0) {
                    params.bollParams = periods.map((p, i) => `${p}_${stdDevs[i] ?? 2}`);
                }
            } else if ('MACD' === indicator) {
                const fastPeriod = config?.fastPeriod ?? 12;
                const slowPeriod = config?.slowPeriod ?? 26;
                const signalPeriod = config?.signalPeriod ?? 9;
                params.macdPeriods = [fastPeriod, slowPeriod, signalPeriod];
            } else if ('KDJ' === indicator) {
                const periods = visibleOrAll();
                if (periods.length > 0) params.kdjPeriods = periods;
            } else if ('CCI' === indicator) {
                const periods = visibleOrAll();
                if (periods.length > 0) params.cciPeriods = periods;
            } else if ('ATR' === indicator) {
                const periods = visibleOrAll();
                if (periods.length > 0) params.atrPeriods = periods;
            } else if ('OBV' === indicator) {
                const periods = visibleOrAll();
                if (periods.length > 0) params.obvPeriods = periods;
            } else if ('ADX' === indicator) {
                const periods = visibleOrAll();
                if (periods.length > 0) params.adxPeriods = periods;
            }
        });

        return params;
    }, [selectedIndicators, indicatorConfigs]);

    useEffect(() => {
        if (visible && item && item.instId && apiKeyId) {
            setManualTimeFrame(undefined);
            setManualLimit(undefined);
            setCurrentLimit(120);
            fetchChartData();
        }
    }, [visible, item, apiKeyId]);

    const fetchChartData = async (overrideTimeFrame?: TimeFrame, overrideLimit?: SampleSize) => {
        if (!item) return;
        setLoading(true);
        setError('');

        try {
            const openTimeMs = normalizeTimestampMs(item.ctime);
            if (!openTimeMs) {
                setError('缺少有效的开仓/委托时间');
                setChartData([]);
                setIndicators(undefined);
                setCurrentEntryCandleTime(undefined);
                return;
            }

            const closeTime = Date.now();
            const durationMs = Math.max(0, closeTime - openTimeMs);

            const timeFrameMsMap: Record<TimeFrame, number> = {
                '1m': 60 * 1000,
                '5m': 5 * 60 * 1000,
                '1h': 60 * 60 * 1000,
                '4h': 4 * 60 * 60 * 1000,
                '1d': 24 * 60 * 60 * 1000
            };

            const hourMs = 60 * 60 * 1000;
            let timeframe: TimeFrame;
            let timeFrameMs: number;

            if (overrideTimeFrame) {
                timeframe = overrideTimeFrame;
                timeFrameMs = timeFrameMsMap[overrideTimeFrame];
            } else {
                if (durationMs < 1 * hourMs) {
                    timeframe = '1m';
                    timeFrameMs = timeFrameMsMap['1m'];
                } else if (durationMs < 5 * hourMs) {
                    timeframe = '5m';
                    timeFrameMs = timeFrameMsMap['5m'];
                } else if (durationMs < 48 * hourMs) {
                    timeframe = '1h';
                    timeFrameMs = timeFrameMsMap['1h'];
                } else {
                    timeframe = '4h';
                    timeFrameMs = timeFrameMsMap['4h'];
                }
            }

            setCurrentTimeFrame(timeframe);

            const limit = overrideLimit ?? currentLimit;
            setCurrentLimit(limit);

            const centerTime = openTimeMs + durationMs / 2;
            const alignedCenterTime = Math.floor(centerTime / timeFrameMs) * timeFrameMs;

            const indicatorWarmupCandles = 35;
            const windowCandlesBefore = Math.floor(limit / 2);
            const startMills = alignedCenterTime - windowCandlesBefore * timeFrameMs - indicatorWarmupCandles * timeFrameMs;
            const finalStartMills = Math.max(0, startMills);

            // 动态构建指标参数
            const indicatorParams = buildIndicatorParams();

            const response = await tradingService.getCompleteChartData({
                instId: item.instId,
                apiKeyId,
                timeframe,
                limit,
                ...indicatorParams,
                startMills: finalStartMills,
            });

            const candles = (response.candles || [])
                .slice()
                .sort((a, b) => a.timestamp - b.timestamp)
                .map((candle: any) => ({
                    timestamp: candle.timestamp,
                    open: candle.open,
                    high: candle.high,
                    low: candle.low,
                    close: candle.close,
                    volume: candle.volume ?? 0,
                    confirm: candle.confirm ?? 1,
                }));
            setChartData(candles);
            setIndicators((response as any).indicators || undefined);

            // 更新当前标记价格（从接口统一获取）
            if (response.markPrice) {
                setCurrentMarkPrice(response.markPrice);
            }

            if (0 === candles.length) {
                setError('未获取到K线数据');
            } else {
                let closest = candles[0].timestamp;
                let minDiff = Math.abs(candles[0].timestamp - openTimeMs);
                for (let i = 1; i < candles.length; i++) {
                    const diff = Math.abs(candles[i].timestamp - openTimeMs);
                    if (diff < minDiff) {
                        minDiff = diff;
                        closest = candles[i].timestamp;
                    }
                }
                setCurrentEntryCandleTime(closest);
            }
        } catch (e) {
            setError('获取K线数据失败');
        } finally {
            setLoading(false);
        }
    };

    const referencePrice = normalizeNumber(item?.referencePrice);
    const openTimeMs = normalizeTimestampMs(item?.ctime);
    const detail = item?.detail || {};
    const strategies = detail.positionStopLossStrategies || detail.position_stop_loss_strategies || [];

    const totalTakeProfitPx = normalizeNumber(detail.totalTakeProfitPrice ?? detail.total_take_profit_price);
    const totalStopLossPx = normalizeNumber(detail.totalStopLossPrice ?? detail.total_stop_loss_price);

    const closeOrderAlgo = Array.isArray(detail.closeOrderAlgo) ? detail.closeOrderAlgo : (Array.isArray(detail.close_order_algo) ? detail.close_order_algo : []);
    const closeOrderAlgoWithTpSl = closeOrderAlgo.find((a: any) =>
        normalizeNumber(a?.tpTriggerPx ?? a?.tp_trigger_px) !== undefined ||
        normalizeNumber(a?.slTriggerPx ?? a?.sl_trigger_px) !== undefined
    );
    const algoTakeProfitPx = normalizeNumber(closeOrderAlgoWithTpSl?.tpTriggerPx ?? closeOrderAlgoWithTpSl?.tp_trigger_px);
    const algoStopLossPx = normalizeNumber(closeOrderAlgoWithTpSl?.slTriggerPx ?? closeOrderAlgoWithTpSl?.sl_trigger_px);

    const totalStrategy = strategies.find((s: any) => s?.isTotal) || strategies.find((s: any) => s?.valid) || (strategies.length > 0 ? strategies[0] : null);
    const strategyTakeProfitPx = normalizeNumber(totalStrategy?.tpTriggerPx ?? totalStrategy?.tp_trigger_px);
    const strategyStopLossPx = normalizeNumber(totalStrategy?.slTriggerPx ?? totalStrategy?.sl_trigger_px);

    const takeProfitPx = totalTakeProfitPx ?? algoTakeProfitPx ?? strategyTakeProfitPx;
    const stopLossPx = totalStopLossPx ?? algoStopLossPx ?? strategyStopLossPx;
    const hasTPSL = takeProfitPx !== undefined || stopLossPx !== undefined;

    const posSide = item?.posSide || 'long';

    const tpslReferenceLines = useMemo(() => {
        const result = {
            avgPx: referencePrice,
            cTime: openTimeMs,
            takeProfitPx: showTPSL ? takeProfitPx : undefined,
            stopLossPx: showTPSL ? stopLossPx : undefined,
            posSide: posSide,
            supportLines: showPivotPoints && pivotPointsData?.supports
                ? pivotPointsData.supports.map((s, index) => ({
                    price: s.price,
                    startTime: pivotPointsData.periodStartTime,
                    endTime: s.basedTime,
                    label: `S${index + 1}`
                }))
                : undefined,
            resistanceLines: showPivotPoints && pivotPointsData?.resistances
                ? pivotPointsData.resistances.map((r, index) => ({
                    price: r.price,
                    startTime: pivotPointsData.periodStartTime,
                    endTime: r.basedTime,
                    label: `R${index + 1}`
                }))
                : undefined,
        };
        return result;
    }, [showTPSL, takeProfitPx, stopLossPx, posSide, referencePrice, openTimeMs, showPivotPoints, pivotPointsData]);

    useEffect(() => {
        if (!hasTPSL) {
            setShowTPSL(false);
        }
    }, [hasTPSL]);

    useEffect(() => {
        if (showPivotPoints && item?.instId && apiKeyId) {
            setPivotPointsData(null);
            fetchPivotPoints();
        }
    }, [currentTimeFrame, currentLimit]);

    // 数据刷新函数引用（用于定时器）
    const fetchChartDataRef = useRef(fetchChartData);
    fetchChartDataRef.current = fetchChartData;

    // 技术指标变化时自动重新拉取K线数据（与页面K线弹窗一致）
    useEffect(() => {
        if (!visible || !item?.instId) return;
        const timeoutId = setTimeout(() => {
            if (fetchChartDataRef.current) {
                fetchChartDataRef.current();
            }
        }, 50);
        return () => clearTimeout(timeoutId);
    }, [selectedIndicators, indicatorConfigs, visible, item?.instId]);

    const handleTimerRefresh = useCallback(async () => {
        if (fetchChartDataRef.current) {
            await fetchChartDataRef.current();
        }
    }, []);

    // 使用 useKlineTimer 实现按时间帧定时拉取K线数据
    const {
        isActive: isTimerActive,
        timeUntilNextUpdate,
        toggle: toggleTimer,
    } = useKlineTimer({
        timeframe: currentTimeFrame,
        onRefresh: handleTimerRefresh,
        enabled: visible,
        onError: () => messageApi.error('K线定时更新失败，请检查网络连接'),
    });

    // 实时标记价格：每30秒拉取一次
    useEffect(() => {
        if (!visible || !item) return;

        const updateMarkPrice = async () => {
            try {
                const response = await tradingService.getMarkPrice(item.instId, apiKeyId);
                if (response?.success && response?.markPrice) {
                    setCurrentMarkPrice(Number(response.markPrice));
                }
            } catch (err) {
            }
        };

        updateMarkPrice();
        const intervalId = setInterval(updateMarkPrice, 30000);
        return () => clearInterval(intervalId);
    }, [visible, item, apiKeyId]);

    // 格式化倒计时显示
    const formatCountdown = useCallback((ms: number): string => {
        if (ms <= 0) return '更新中';

        const totalSeconds = Math.ceil(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const parts: string[] = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);
        if (seconds > 0 || 0 === parts.length) parts.push(`${seconds}s`);

        return parts.join(' ');
    }, []);

    return (
        <Modal
            title={
                <div>
                    {/* 第一行：合约信息 + 控制按钮 */}
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                        <Text style={{color: 'long' === posSide ? '#00d97e' : '#ff5252', whiteSpace: 'nowrap'}}>
                            {item?.instId}
                        </Text>
                        <Text style={{color: '#8c8c8c', fontSize: 12, whiteSpace: 'nowrap'}}>
                            {currentTimeFrame} · {currentLimit}
                        </Text>
                        {item?.type === 'position' && (
                            <Text style={{color: '#8c8c8c', fontSize: 12, marginLeft: 8, whiteSpace: 'nowrap'}}>
                                开仓均价: {referencePrice !== undefined ? referencePrice.toFixed(4) : '-'} ·
                                开仓时间: {openTimeMs ? new Date(openTimeMs).toLocaleString() : '-'}
                            </Text>
                        )}
                        <Segmented
                            size="small"
                            value={manualTimeFrame ?? currentTimeFrame}
                            options={(['1m', '5m', '1h', '4h', '1d'] as TimeFrame[]).map(tf => ({
                                label: tf,
                                value: tf,
                                disabled: loading || (tf === (manualTimeFrame ?? currentTimeFrame)),
                            }))}
                            onChange={(value) => {
                                const next = value as TimeFrame;
                                if (loading) return;
                                const current = manualTimeFrame ?? currentTimeFrame;
                                if (next === current) return;
                                setManualTimeFrame(next);
                                fetchChartData(next, manualLimit ?? currentLimit);
                            }}
                            style={{marginLeft: 8}}
                        />
                        <Segmented
                            size="small"
                            value={manualLimit ?? currentLimit}
                            options={([30, 60, 90, 120, 150] as SampleSize[]).map(limit => ({
                                label: String(limit),
                                value: limit,
                                disabled: loading || (limit === (manualLimit ?? currentLimit)),
                            }))}
                            onChange={(value) => {
                                const next = value as SampleSize;
                                if (loading) return;
                                const current = manualLimit ?? currentLimit;
                                if (next === current) return;
                                setManualLimit(next);
                                setCurrentLimit(next);
                                fetchChartData(manualTimeFrame ?? currentTimeFrame, next);
                            }}
                            style={{marginLeft: 8}}
                        />
                        <Segmented
                            size="small"
                            value={showTPSL ? 'show' : 'hide'}
                            options={[
                                {label: 'TP/SL', value: 'show'},
                                {label: 'Hide', value: 'hide'},
                            ]}
                            disabled={!hasTPSL}
                            onChange={(value) => {
                                setShowTPSL(value === 'show');
                                setTpslReloadKey(k => k + 1);
                            }}
                            style={{marginLeft: 8}}
                        />
                        <Segmented
                            size="small"
                            value={showAvgPx ? 'show' : 'hide'}
                            options={[
                                {label: '开仓', value: 'show'},
                                {label: 'Hide', value: 'hide'},
                            ]}
                            onChange={(value) => setShowAvgPx(value === 'show')}
                            style={{marginLeft: 8}}
                        />
                        <Segmented
                            size="small"
                            value={showPivotPoints ? 'show' : 'hide'}
                            options={[
                                {label: loadingPivot ? '计算中...' : '支撑&突破', value: 'show'},
                                {label: 'Hide', value: 'hide'},
                            ]}
                            onChange={(value) => {
                                if ('show' === value && !pivotPointsData) {
                                    fetchPivotPoints();
                                } else {
                                    setShowPivotPoints(value === 'show');
                                }
                            }}
                            style={{marginLeft: 8}}
                        />
                    </div>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1350}
            styles={{
                body: {backgroundColor: '#0d1117', padding: 0},
                header: {
                    backgroundColor: '#0d1117',
                    color: '#ffffff',
                    borderBottom: '1px solid rgba(255,255,255,0.07)',
                    padding: '8px 12px'
                }
            }}
            style={{color: '#ffffff'}}
        >
            <div style={{display: 'flex', flexDirection: 'column', height: 600}}>
                <CompactIndicatorBar
                    selectedIndicators={selectedIndicators}
                    indicatorConfigs={indicatorConfigs}
                    onToggleIndicator={toggleIndicator}
                    onUpdateIndicatorConfig={updateIndicatorConfig}
                    onClearAllIndicators={clearAllIndicators}
                />
                {loading ? (
                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1}}>
                        <Spin size="large"/>
                    </div>
                ) : error ? (
                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1}}>
                        <Text style={{color: '#ff5252'}}>{error}</Text>
                    </div>
                ) : chartData.length > 0 ? (
                    <div style={{flex: 1, position: 'relative', minHeight: 0}}>
                        {/* 价格覆盖层（右上角） */}
                        <div className="chart-price-overlay">
                        <span className={`price-value ${isPriceUp ? 'up' : 'down'}`}>
                            {displayPrice != null ? displayPrice.toFixed(2) : '--'}
                        </span>
                            {priceChangePercent != null && (
                                <span className={`price-change ${priceChangePercent >= 0 ? 'up' : 'down'}`}>
                                {priceChangePercent >= 0 ? '+' : ''}{priceChangePercent.toFixed(2)}%
                            </span>
                            )}
                        </div>
                        {/* 定时器状态（右上角） */}
                        <div className="chart-timer-overlay">
                        <span
                            className={`status-dot ${isTimerActive ? 'active' : 'inactive'}`}
                            onClick={toggleTimer}
                            title={isTimerActive ? '定时更新已启用' : '定时更新已暂停'}
                        />
                            <span className="status-countdown">
                            {formatCountdown(timeUntilNextUpdate)}
                        </span>
                        </div>
                        <LightweightCandlestickChart
                            key={`chart-${showTPSL ? 'show' : 'hide'}-${tpslReloadKey}-${paneReloadKey}`}
                            data={bars}
                            maxVisibleBars={currentLimit}
                            timeFrame={currentTimeFrame}
                            markPrice={currentMarkPrice || normalizeNumber(item?.referencePrice)}
                            markPriceColor="#f5a623"
                            indicators={transformIndicatorsForChart(indicators)}
                            visibleIndicators={visibleIndicators}
                            referenceLines={tpslReferenceLines}
                            showAvgPx={showAvgPx}
                        />
                    </div>
                ) : (
                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1}}>
                        <Text style={{color: '#8c8c8c'}}>暂无K线数据</Text>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ActivePositionOrderChartModal;
